import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { GridContainer, Row, Column, withResponsive } from 'mage-components';

import { Message, translator } from 'mage-i18n';
import { SpacerVertical, ButtonPrimary, Typography, Separator } from '@klarna/bubble-ui';
import { MOET_CONSTANTS } from 'moet-core';

import { APP_PATHS } from '@/constants';
import { STAKEHOLDER_ROLES_V2, STAKEHOLDER_TYPES } from '@/containers/withStakeholderLogic';
import {
  getDataForStoreDetailsSummary,
  getMerchantStatus,
  hasAnyStakeholderWithRoles,
  isManagedMerchant,
  isMerchantOnboardingRejected,
  isMerchantRegistered,
} from '@/selectors/merchant';
import { isUnmanagedKyc } from '@/selectors/widget';
import { TranslatedSidebarBlock } from '@/components/shared-deprecated';
import { MidIndicator, SummaryStatus } from '@/components';
import PageHeader from '@/components/PageHeader';

import SummaryBankAccounts from './ApplicationOverview/SummaryBankAccounts';
import SummaryBusinessOwners from './ApplicationOverview/SummaryBusinessOwners';
import SummaryStakeholders from './ApplicationOverview/SummaryStakeholders';
import SummaryStoreDetails from './ApplicationOverview/SummaryStoreDetails';

const tKey = 'products-fe.pages.summary_page';
const { ONBOARDING_STATUSES_V2 } = MOET_CONSTANTS;

const ApplicationOverview = ({
  storeDetailsForSummary,
  history,
  isManagedMerchant,
  isMerchantOnboardingRejected,
  isUnmanagedKyc,
  showStakeholderSection,
  status,
  t,
  isMerchantRegistered = false,
  basePath,
}) => {
  useEffect(() => {
    if (!isUnmanagedKyc) {
      window.onpopstate = e => {
        // we check for pathname to see if the user is tryong to navigate back
        // if yes, don't redirect to application confirmation page and stay on the current page.
        const pathname = e?.target?.location?.pathname;
        if (pathname && !pathname.includes(APP_PATHS.APPLICATION_OVERVIEW_PATH)) {
          history.push(`${basePath}/${APP_PATHS.APPLICATION_OVERVIEW_PATH}`);
        }
      };
    }
  });

  return (
    <GridContainer padding={{ mobile: 4 }}>
      <Row>
        <PageHeader
          title={t(`${tKey}.header_new.subtitle`)}
          description={
            isMerchantOnboardingRejected
              ? t(`${tKey}.header_new.rejection`)
              : t(`${tKey}.header_new.title`)
          }
          backNavigation={{ path: '', label: t(`${tKey}.main.buttons.back`) }}
        >
          <Column>
            <Typography textToken="text-style/headings/titles/medium/tertiary">
              <Message path={`${tKey}.header_new.subtitle`} />
            </Typography>
            <Typography textToken="text-style/headings/titles/bold/primary">
              {isMerchantOnboardingRejected ? (
                <Message path={`${tKey}.header_new.rejection`} />
              ) : (
                <Message path={`${tKey}.header_new.title`} />
              )}
            </Typography>
            <SpacerVertical spaceToken="space/150" />
            <MidIndicator />
            <SpacerVertical spaceToken="space/600" />
            <SummaryStatus />
            <SpacerVertical spaceToken="space/600" />
            <Typography textToken="text-style/headings/blocks/bold/tertiary">
              <Message path={`${tKey}.sidebar_left.application_details`} />
              <SpacerVertical spaceToken="space/600" />
            </Typography>
            <SpacerVertical spaceToken="space/150" />
          </Column>
        </PageHeader>
      </Row>
      <Row>
        <Column columns={{ tablet: 3 }}>
          <TranslatedSidebarBlock tKeyPrefix={`${tKey}.sidebar_left.store_details`} />
        </Column>
        <Column columns={{ tablet: 9 }}>
          <Separator />
          <SpacerVertical spaceToken="space/300" />
          <SummaryStoreDetails
            storeDetails={storeDetailsForSummary}
            isUnmanagedKyc={isUnmanagedKyc}
          />
          <SpacerVertical spaceToken="space/600" />
          {!isMerchantRegistered && status !== ONBOARDING_STATUSES_V2.COMPLETED && (
            <div>
              <Typography textToken="text-style/headings/titles/bold/grande">
                <Message path={`${tKey}.main.store_details.business_owners.title`} />
              </Typography>
              <SummaryBusinessOwners
                noElementsTKey={`${tKey}.main.store_details.business_owners.no_elements`}
              />{' '}
            </div>
          )}
        </Column>
      </Row>
      {!isManagedMerchant && status !== ONBOARDING_STATUSES_V2.COMPLETED && (
        <Row>
          <Column columns={{ tablet: 3 }}>
            <TranslatedSidebarBlock tKeyPrefix={`${tKey}.sidebar_left.payouts`} />
          </Column>
          <Column columns={{ tablet: 9 }}>
            <Separator />
            <SpacerVertical spaceToken="space/300" />
            <SummaryBankAccounts
              type="defaultBankAccounts"
              noElementsTKey={`${tKey}.main.payouts.default_bank_accounts.no_elements`}
              showRoutingNumberEU
            />
            <SpacerVertical spaceToken="space/300" />
            <SummaryBankAccounts
              type="additionalBankAccounts"
              noElementsTKey={`${tKey}.main.payouts.additional_bank_accounts.no_elements`}
            />
            <SpacerVertical spaceToken="space/600" />
            <SpacerVertical spaceToken="space/800" />
          </Column>
        </Row>
      )}
      {showStakeholderSection && (
        <Row>
          <Column columns={{ tablet: 3 }}>
            <TranslatedSidebarBlock tKeyPrefix={`${tKey}.sidebar_left.stakeholders`} />
          </Column>
          <Column columns={{ tablet: 9 }}>
            <Separator />
            <SpacerVertical spaceToken="space/300" />
            <SummaryStakeholders
              type={STAKEHOLDER_TYPES.PERSON}
              role={STAKEHOLDER_ROLES_V2.ULTIMATE_BENEFICIAL_OWNER}
            />
            <SummaryStakeholders
              type={STAKEHOLDER_TYPES.PERSON}
              role={STAKEHOLDER_ROLES_V2.APPLICANT}
            />
            <SummaryStakeholders
              type={STAKEHOLDER_TYPES.PERSON}
              role={STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT}
            />
            <SpacerVertical spaceToken="space/800" />
          </Column>
        </Row>
      )}
      {isUnmanagedKyc && (
        <Row>
          <Column alignItems={{ mobile: 'flex-end' }}>
            <ButtonPrimary
              onClick={() => history.push(APP_PATHS.WHATS_NEXT_PATH + history.location.search)}
            >
              {t(`${tKey}.main.buttons.back`)}
            </ButtonPrimary>
          </Column>
        </Row>
      )}
    </GridContainer>
  );
};

ApplicationOverview.propTypes = {
  basePath: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  isManagedMerchant: PropTypes.bool.isRequired,
  isMerchantOnboardingRejected: PropTypes.bool.isRequired,
  isUnmanagedKyc: PropTypes.bool.isRequired,
  showStakeholderSection: PropTypes.bool.isRequired,
  storeDetailsForSummary: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  isMerchantRegistered: PropTypes.bool,
  status: PropTypes.string,
};

export default connect(state => ({
  status: getMerchantStatus(state),
  storeDetailsForSummary: getDataForStoreDetailsSummary(state),
  isManagedMerchant: isManagedMerchant(state),
  isMerchantRegistered: isMerchantRegistered(state),
  isMerchantOnboardingRejected: isMerchantOnboardingRejected(state),
  isUnmanagedKyc: isUnmanagedKyc(state),
  showStakeholderSection: hasAnyStakeholderWithRoles([
    STAKEHOLDER_ROLES_V2.ULTIMATE_BENEFICIAL_OWNER,
    STAKEHOLDER_ROLES_V2.APPLICANT,
    STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT,
  ])(state),
  t: translator(state),
}))(withResponsive(ApplicationOverview));
